// A loose clip's tempo and type, guessed from its name and its length. Pure.
//
// Loops are made to repeat, so they are cut to a whole number of bars — and
// at a whole-number tempo that pins the tempo down: a 4.0 s loop is 1 bar at
// 60, 2 bars at 120, 4 at 240. Within one octave (80–160, where the room's
// own detector folds too) at most one of those is a whole BPM. A name that
// states a tempo ("Drums 128bpm") wins over the length.
//
// One-shots (hits, stabs, risers) have no tempo worth recording.

import { bpmFromName } from "./naming";

export type ClipKind = "loop" | "oneshot";
export type ClipBpmSource = "name" | "length" | null;

export interface ClipGuess {
  bpm: number | null;
  bpmSource: ClipBpmSource;
  kind: ClipKind;
}

const BAR_COUNTS = [1, 2, 4, 8, 16, 32] as const;
const FOLD_MIN = 80;
const FOLD_MAX = 160;
/**
 * How far from a whole BPM a length may read and still be a cut loop. DAW
 * exports are sample-accurate, so a real loop lands within a few thousandths;
 * a looser bar would also pass about one in ten arbitrary lengths.
 */
const WHOLE_BPM = 0.01;
/** Shorter than this with no tempo of its own: a hit, not a loop. */
const ONESHOT_MAX_SEC = 1.5;

/** The whole-number tempo at which this length is a whole number of 4/4 bars, or null. */
export function bpmFromLoopLength(durationSec: number): number | null {
  if (!(durationSec > 0)) return null;
  for (const bars of BAR_COUNTS) {
    const bpm = (240 * bars) / durationSec;
    if (bpm < FOLD_MIN || bpm >= FOLD_MAX) continue;
    const whole = Math.round(bpm);
    if (Math.abs(bpm - whole) <= WHOLE_BPM) return whole;
  }
  return null;
}

/** Bars at a tempo (4/4), to two decimals. */
export function barsOf(durationSec: number, bpm: number): number {
  return Math.round(((durationSec * bpm) / 240) * 100) / 100;
}

export function guessClip(name: string, durationSec: number): ClipGuess {
  const named = bpmFromName(name);
  const fromLength = bpmFromLoopLength(durationSec);
  if (named !== null) {
    // A stated tempo on something at least a beat long reads as a loop.
    const kind: ClipKind = (durationSec * named) / 60 >= 1 ? "loop" : "oneshot";
    return { bpm: named, bpmSource: "name", kind };
  }
  if (fromLength !== null && durationSec >= ONESHOT_MAX_SEC) return { bpm: fromLength, bpmSource: "length", kind: "loop" };
  return { bpm: null, bpmSource: null, kind: "oneshot" };
}
